/**
 * The undo/redo toast. Every toast remembers the state to go back to; pressing its button
 * restores that state AND remembers the state it just left, so the next press goes the other
 * way: Undo → Redo → Undo … Never a button that restores what's already there.
 */

export interface UndoToast<S> {
  readonly id: number;
  /** The edit as she'd describe it: "C6 planned for Entrance". */
  readonly edit: string;
  /** What the button does now. */
  readonly action: "undo" | "redo";
  /** The state the button restores. */
  readonly restoreTo: S;
}

export function toastFor<S>(edit: string, before: S, id: number): UndoToast<S> {
  return { id, edit, action: "undo", restoreTo: before };
}

/** What the toast says, given what its button will do next. */
export function toastText<S>(toast: UndoToast<S>): string {
  return toast.action === "undo" ? toast.edit : `Undone: ${toast.edit}`;
}

/**
 * Press the button: returns the state to restore now, and the toast to show next
 * (which flips direction and remembers `current`, the state being left behind).
 */
export function pressToast<S>(toast: UndoToast<S>, current: S, id: number): { restore: S; next: UndoToast<S> } {
  return {
    restore: toast.restoreTo,
    next: { id, edit: toast.edit, action: toast.action === "undo" ? "redo" : "undo", restoreTo: current },
  };
}
